import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/**
 * Site ids in the caller's org with DataBridge auto-classification enabled
 * (POS2-253). The Transactions page disables manual Analyse for these sites —
 * the background worker already classifies their events, so a manual run is
 * duplicate AI spend. One bounded request (scales with enabled configs, not
 * the sites on the page), so it covers the all-sites feed cheaply.
 */
export function useEnabledClassificationSites() {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<Set<string>>({
    queryKey: ["classification-enabled-sites"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/classification-enabled-sites`, {
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: { site_ids: string[] } };
      return new Set(body.data.site_ids);
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    staleTime: 60_000,
  });
}
